import { Theme, makeStyles, createStyles, useTheme, Button, Typography } from '@material-ui/core';
import React, { useContext, FC, useCallback, useState, useEffect, CSSProperties } from 'react';
import { FormContext, useDataFieldValidity, useEditability, useDataValue, useExpressionValue, FormControlProps } from '..';
import Select from 'react-select';
import { ListControlDefinition } from '../services/layout';
import { FormControl } from './FormControl';
import { StyleRules } from '@material-ui/styles';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { useConfirmation } from '../ConfirmContext';

// export type Breakpoint = 'xl' | 'lg' | 'md' | 'sm' | 'xs';
export type BreakpointDirection = 'up' | 'down';

export type BreakpointMappedCSSProperties = {
  [P in Breakpoint]?: {
    [K in BreakpointDirection]?: {
      [key: string]: CSSProperties
    }
  }
}

export interface ExtendedListControlDefinition extends ListControlDefinition {
  wrapperStyles: BreakpointMappedCSSProperties;
  beforeChange?: {
    from: string;
    to: string;
    prompt: {
      title: string;
      text: string;
    }
  }[];
}

export interface ListProps extends FormControlProps<ExtendedListControlDefinition> {
  definition: ExtendedListControlDefinition;
  onChange?: (newValue?: string | string[]) => void;
}

/**
 * Apply arbitrary styles at specified breakpoints
 * 
 * @usage
 * ```ts
 * const stylesFromDefinition = {
 *  md: {
 *    down: {
 *      minWidth: 45%
 *    }
 *  }
 * }
 * const styles = toCSSStyles(theme, stylesFromDefinition);
 * // returns
 * // {
 * //   theme.breakpoints.down('md'): {
 * //     minWidth: '45%'
 * //   }
 * // }
 * ```
 */
export const toCSSStyles = (theme: Theme, properties: BreakpointMappedCSSProperties) => {
    
  if (!properties) {
    return {};
  }

  const mappedProperties: {[key: string]: CSSProperties} = {};

  Object.keys(properties)
    .forEach(breakpoint => {
      Object.keys((properties as any)[breakpoint])
        .forEach(direction => {
          const breakpointName = (theme.breakpoints as any)[direction](breakpoint);
          mappedProperties[breakpointName] = (properties as any)?.[breakpoint]?.[direction] || {};
        })
    }, {})
 
  return mappedProperties;
}

const useStyles = makeStyles(function (theme: Theme) {
  const { palette, shape, spacing } = theme;

  return createStyles({
    root: {
      marginTop: spacing(1),
      marginBottom: spacing(1),
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    options: {
      display: 'flex',
      flex: 1,
      alignItems: 'stretch',
    },
    buttonWrapper: (props: ListProps) => ({
      padding: spacing(1),
      height: '44px',
      alignContent: 'stretch',
      display: 'flex',
      flex: '1',
      ...toCSSStyles(theme, props.definition.wrapperStyles)
    }),
    button: {
      padding: spacing(1),
      flex: 1,
      textTransform: 'none',
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      }
    },
    buttonText: {
      fontWeight: 'bold',
      lineHeight: 1.1,
    },
    inline: {
      padding: spacing(1),
      marginTop: spacing(1),
      flex: 1,
    },
    formControl: {
      marginLeft: spacing(1),
      borderRadius: shape.borderRadius,
    },
    multiValueRemove: {
      '&:hover': {
        backgroundColor: palette.secondary.main,
      },
    },
    gridOptions: {
      display: 'flex',
      flexWrap: 'wrap',
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'flex-end',
      }
    },
    picker: {
      flex: 1,
    },
    small: {
      fontSize: '0.75rem'
    }
  });
});
// Styles for the Picker variant's components
const makeSelectStyles = function (theme: Theme) {
  return ({
    menu: function (base: StyleRules) {
      return (
        Object.assign(
          Object.assign({}, base),
          {
            marginTop: -theme.spacing(1),
            boxShadow: theme.shadows[2]
          }
        )
      );
    },
    multiValue: function (base: StyleRules) {
      const _a = theme.typography.body1, fontFamily = _a.fontFamily, fontSize = _a.fontSize, fontWeight = _a.fontWeight;
      return Object.assign(
        Object.assign({}, base),
        {
          fontFamily: fontFamily,
          fontSize: fontSize,
          fontWeight: fontWeight
        }
      );
    },
    container: function (base: StyleRules) {
      return (
        Object.assign(
          Object.assign({}, base),
          {
            paddingTop: '1px',
            padding: theme.spacing(0, 1),
            marginTop: 0
          }
        )
      );
    },
    control: function (base: StyleRules, state: StyleRules) {
      return (
        Object.assign(
          Object.assign({}, base),
          {
            boxShadow: state.isFocused ? theme.shadows[0] : base.boxShadow,
            '&:hover': {
              boxShadow: state.isFocused ? theme.shadows[0] : base.boxShadow,
            }
          }
        )
      );
    },
  });
};

/**
 * Handle a toggle event for a single-select button group. If the element tapped was selected prior,
 * the change results in the unsetting/removal of the selection.
 * @param selected the string value that was selected
 */
function singleToggle(currentSelection: string, selected: string) {
  return currentSelection === selected ? null : selected;
}
function multiToggle(currentSelection: string[], selected: string) {
  let newSelection;
  if (currentSelection === null || currentSelection.indexOf(selected) < 0) {
    // Toggle on
    newSelection = [...(currentSelection === null ? [] : currentSelection), selected ];
  } else {
    // Toggle off
    newSelection = [...currentSelection];
    newSelection.splice(currentSelection.indexOf(selected), 1);
    if (newSelection.length === 0)
      newSelection = null;
  }
  return newSelection;
}
export const HOOK_ACTIONS = 'prompt';

export const List: FC<ListProps> = function (props) {
  const definition = props.definition;
  const beforeChange = definition.beforeChange;
  const dataPath = definition.dataPath;
  const theme = useTheme();
  const classes = useStyles(props);
  const { onSetData, onRemoveData, scope } = useContext(FormContext);
  const error = useDataFieldValidity(dataPath);
  const editable = useEditability(definition.editable);
  const selection = useDataValue<string | string[]>(definition.dataPath) || null;
  const options = useExpressionValue<string[]>(definition.options);
  const [ selectStyles, setSelectStyles ] = useState(makeSelectStyles(theme));
  const [value, setValue] = useState();
  const confirm = useConfirmation();

  useEffect(() => {
    const newValue = options?.filter(function(value) {
      return definition.multiselect
      ? selection !== null && selection.includes(value)
      : selection === value;
    });
    setValue(newValue as any);
  }, [selection, definition.multiselect, options])

  const applyChanges = useCallback((selected: string) => {
    const newSelection = definition.multiselect
      ? multiToggle(selection as string[], selected)
      : singleToggle(selection as string, selected);
    if (newSelection === null) {
      onRemoveData(dataPath, scope);
    } else {
      onSetData(dataPath, scope, newSelection);
    }
  }, [selection, dataPath, definition, onRemoveData, onSetData, scope])

  /**
   * If the field implements a 'beforeChange' hook, only apply the changes if the user agrees in the confirmation dialog
   */
  const handleSelection = useCallback((selected: string | string[], scope: string) => {
    if (beforeChange) {
      const hook = beforeChange.find(hook => selected === hook.to && selection === hook.from);

      if (hook) {
        confirm({
          variant: "danger",
          catchOnCancel: true,
          title: hook.prompt.title,
          text: hook.prompt.text
        })
          .then(() => {
            applyChanges(selected as string);
          })
          .catch((e) => {
            console.warn('Unable to invoke beforeChange hook:', e);
          });
      } else {
        applyChanges(selected as string);
      }
    } else {
      applyChanges(selected as string);
    }
  }, [selection, applyChanges, beforeChange, confirm, definition]);

  const handleChange = useCallback((newValue: any, _: any) => {
    const newSelection = newValue;
    if (newSelection && newSelection.length) {
      onSetData(dataPath, scope, newSelection);
    } else {
      onRemoveData(dataPath, scope);
    }
  }, [dataPath, scope, onRemoveData, onSetData])

  const isInline = !definition.variant || definition.variant === 'inline';
  let control;
  if (isInline) {
    control = options === null
      ? undefined
      : (
          <div className = { classes.gridOptions }>
            { options!.map(function (option, index: number) {
              const isSelected = selection !== null &&
                (Array.isArray(selection)
                  ? selection.indexOf(option) >= 0
                  : selection === option);

              return (
                <div className = { classes.buttonWrapper } key = { index }>
                  <Button
                    className = { classes.button }
                    color = { 'secondary' }
                    variant = { isSelected ? 'contained' : 'outlined' }
                    onClick = { () => handleSelection(option, scope) }
                    disabled = { !editable }>
                    <Typography
                      variant = { 'body1' }
                      color = { 'inherit' }
                      className = { classes.buttonText }>
                      { option }
                    </Typography>
                  </Button>
                </div>
              );
            })}
          </div>
        )
  } else {
    // Picker
    // const valueFilter = function (value: string) {
    //   return definition.multiselect
    //     ? selection !== null && selection.includes(value)
    //     : selection === value;
    // };

    // types are messed up
    control = options === null
      ? undefined
      : (
        // duplicate keys in children
          <div>
            <Select
              aria-label = { definition.label }
              options = { options as any }
              isMulti = { definition.multiselect }
              getOptionLabel = { ((o: string) => o as any) as any }
              getOptionValue = { ((o: string) => o as any) as any }
              isClearable
              isDisabled = { !editable }
              // styles = { selectStyles(theme) as any }
              styles = { selectStyles as any }
              closeMenuOnSelect = { !definition.multiselect }
              // value = { options?.filter(valueFilter) as any }
              value = { value as any }
              onChange = { handleChange }
              // onChange = { function (newValue: string) {
              //   const newSelection = newValue;
              //   if (newSelection && newSelection.length) {
              //     onSetData(dataPath, scope, newSelection);
              //   } else {
              //     onRemoveData(dataPath, scope);
              //   }
              // } as any}
              theme = { function (base: Theme) {
                return Object.assign(
                  Object.assign({}, base),
                  {
                    borderRadius: theme.shape.borderRadius,
                    colors: Object.assign(
                      Object.assign({}, (base as any).colors),
                      {
                        primary: theme.palette.secondary.main,
                        primary75: '#4BAD67',
                        primary50: '#8ECCA0',
                        primary25: '#D2EAD9',
                        danger: theme.palette.secondary.dark,
                        dangerLight: theme.palette.secondary.light
                      }
                    )
                  }
                );
              } as any}
              // theme = { function (base: any) {
              //   return Object.assign(
              //     Object.assign({}, base),
              //     { borderRadius: theme.shape.borderRadius,
              //       colors: Object.assign(
              //         Object.assign({}, base.colors),
              //         {
              //           primary: theme.palette.secondary.main,
              //           primary75: "#4BAD67",
              //           primary50: "#8ECCA0",
              //           primary25: "#D2EAD9",
              //           danger: theme.palette.secondary.dark,
              //           dangerLight: theme.palette.secondary.light
              //         })
              //       })
              //     }
              // }
              className = { classes.picker }>
            </Select>
          </div>
        );
  }

  return (
    <FormControl
      label = { definition.label }
      helpText = { definition.helpText }
      footnote = { definition.footnote }
      style = { definition.style }
      error = { error }>
      { control }
    </FormControl>
  )
};

// registry.registerComponent('List', List);
